import { Injectable, OnDestroy } from '@angular/core';
import { LIMIT } from '@authority-labs/constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

const STORAGE_LEADER_ID = 'notification_leader_id';
const STORAGE_HEARTBEAT = 'notification_leader_heartbeat';

@Injectable({
  providedIn: 'root'
})
export class TabLeaderService implements OnDestroy {
  private readonly tabId = Math.random().toString(36).substring(2);
  private readonly isLeaderSubject = new BehaviorSubject<boolean>(false);
  private heartbeatInterval: ReturnType<typeof setInterval>;
  private checkInterval: ReturnType<typeof setInterval>;
  private channel: BroadcastChannel;
  readonly isLeader$: Observable<boolean> = this.isLeaderSubject.pipe(
    distinctUntilChanged()
  );
  private beforeUnloadHandler = () => this.resign();

  constructor() {
    this.initChannel();
    this.tryClaimLeadership();
    this.startLeaderCheck();
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  get isLeader(): boolean {
    return this.isLeaderSubject.value;
  }

  ngOnDestroy(): void {
    this.resign();
    this.cleanup();
  }

  private initChannel(): void {
    this.channel = new BroadcastChannel(LIMIT.LEADER_CHANNEL_NAME);
    this.channel.onmessage = (event) => {
      const { type } = event.data;
      if (type === 'leader-resigned' && !this.isLeaderSubject.value) {
        this.tryClaimLeadership();
      }
    };
  }

  private tryClaimLeadership(): void {
    const existingId = localStorage.getItem(STORAGE_LEADER_ID);
    const heartbeat = parseInt(
      localStorage.getItem(STORAGE_HEARTBEAT) || '0',
      10
    );
    const isStale = Date.now() - heartbeat > LIMIT.LEADER_TIMEOUT_MS;

    if (!existingId || isStale) {
      this.claimLeadership();
    }
  }

  private claimLeadership(): void {
    localStorage.setItem(STORAGE_LEADER_ID, this.tabId);
    localStorage.setItem(STORAGE_HEARTBEAT, Date.now().toString());

    // Verify we won the race
    setTimeout(() => {
      if (localStorage.getItem(STORAGE_LEADER_ID) === this.tabId) {
        this.isLeaderSubject.next(true);
        this.startHeartbeat();
      }
    }, 50);
  }

  private startHeartbeat(): void {
    clearInterval(this.heartbeatInterval);
    this.heartbeatInterval = setInterval(() => {
      if (!this.isLeaderSubject.value) {
        return;
      }
      // Check if another tab has taken over leadership
      if (localStorage.getItem(STORAGE_LEADER_ID) !== this.tabId) {
        this.isLeaderSubject.next(false);
        clearInterval(this.heartbeatInterval);
        return;
      }
      localStorage.setItem(STORAGE_HEARTBEAT, Date.now().toString());
    }, LIMIT.LEADER_HEARTBEAT_MS);
  }

  private startLeaderCheck(): void {
    this.checkInterval = setInterval(() => {
      if (this.isLeaderSubject.value) {
        return;
      }
      const heartbeat = parseInt(
        localStorage.getItem(STORAGE_HEARTBEAT) || '0',
        10
      );
      if (Date.now() - heartbeat > LIMIT.LEADER_TIMEOUT_MS) {
        this.claimLeadership();
      }
    }, LIMIT.LEADER_TIMEOUT_MS);
  }

  private resign(): void {
    if (!this.isLeaderSubject.value) {
      return;
    }
    this.isLeaderSubject.next(false);
    clearInterval(this.heartbeatInterval);
    localStorage.removeItem(STORAGE_LEADER_ID);
    localStorage.removeItem(STORAGE_HEARTBEAT);
    try {
      this.channel.postMessage({ type: 'leader-resigned' });
    } catch {
      // Channel may already be closed
    }
  }

  private cleanup(): void {
    clearInterval(this.heartbeatInterval);
    clearInterval(this.checkInterval);
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    try {
      this.channel.close();
    } catch {
      // Already closed
    }
  }
}
